import { Component } from '@angular/core';
import { AppService } from '@app/app.service';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { loggerFor } from '@app/utils/logger';
import { toggleClass } from '@app/utils/toggle-class';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectConfig } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';
import { delay, map, withLatestFrom } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { collectErrors, isHttpErrorResponse } from './utils/error/portal-error';

@UntilDestroy()
@Component({
  selector: 'portal-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent {
  delayedProgress$ = this.loader.value$.pipe(
    untilDestroyed(this),
    delay(1000),
    withLatestFrom(this.loader.value$),
    map((v) => v[1])
  );

  logger = loggerFor('AppComponent');

  constructor(
    private appService: AppService,
    private authenticationService: AuthenticationService,
    private loader: LoadingBarService,
    private ngSelectConfig: NgSelectConfig,
    private ngbModal: NgbModal,
    private translateService: TranslateService
  ) {
    this.logger.info('Initializing...');

    this.appService.initialize().subscribe({
      complete: () => {
        this.logger.info('Initialization completed');

        this.hideApplicationLoader();
      },
      error: (err) => {
        this.logger.error('Initialization error', err);
        let message: string;

        const httpErrors = collectErrors(err, isHttpErrorResponse);

        if (httpErrors.length > 0) {
          // Check for status codes: 502 (Bad Gateway) and 504 (Gateway Timeout) and
          if (httpErrors.some((e) => e.status === 502 || e.status === 504)) {
            // TODO if there is a problem with backend then translations are not loaded, hence using specific text here instead of using translation service.
            // Need to look into it why the default translations file is not loaded from the assets.
            message = `
              Das System ist derzeit aufgrund von Wartungsarbeiten nicht verfügbar, bitte versuchen Sie es später erneut.
              <br>
              System is currently unavailable due to maintenance. Please try again later.
              `;
          } else {
            err = httpErrors[0];
            message = `${err.status} ${err.statusText || ''}`;
          }
        } else if (err.message) {
          message = err.message;
        } else if (err.cause == null || err.i18n === 'general.err.maintenance') {
          message = `
              Das System ist derzeit aufgrund von Wartungsarbeiten nicht verfügbar, bitte versuchen Sie es später erneut.
              <br>
              System is currently unavailable due to maintenance. Please try again later.
              `;
        } else {
          message = err;
        }
        const initErrorEl = document.getElementById('initError');

        initErrorEl.innerHTML = `${message}`;

        if (httpErrors[0]?.error?.data?.indexOf('<html') > -1 && environment.production === false) {
          const div = document.createElement('div');
          div.setAttribute(
            'style',
            `display: none; position: fixed; top: 1.5rem; height: calc(100vh - 3rem); width: calc(100vw - 1rem); left: 50%; transform: translateX(-50%);`
          );

          const iframe = document.createElement('iframe');
          iframe.setAttribute('style', 'height: 100%; width: 100%;');

          const closeAction = document.createElement('div');
          closeAction.setAttribute('role', 'button');
          closeAction.innerHTML = 'Close';
          closeAction.addEventListener('click', hide);

          div.appendChild(closeAction);
          div.appendChild(iframe);
          initErrorEl.appendChild(div);
          initErrorEl.addEventListener('click', show);

          initErrorEl.style.cursor = 'pointer';
          initErrorEl.style.textDecoration = 'underline';
          initErrorEl.classList.add('text-danger');

          requestAnimationFrame(() => {
            const contentDocument = iframe.contentDocument.open();
            contentDocument.write(httpErrors[0]?.error?.data);
            contentDocument.close();
          });

          function hide(event: Event) {
            event.stopPropagation();
            div.style.display = 'none';
          }

          function show() {
            div.style.display = 'block';
          }
        }
      },
    });

    // Update the "app--unauthenticated" class whenever the authentication changes
    this.authenticationService.isAuthenticated$.subscribe((authenticated) => {
      toggleClass('app--unauthenticated', document.body, authenticated === false);
    });

    this.authenticationService.onLogout$.subscribe(() => {
      this.ngbModal.dismissAll('User logged out');
    });

    this.appService.version.data$.subscribe((portalVersion) => {
      const changed = portalVersion.version !== environment.version;
      toggleClass('app--show-new-version-container', document.body, changed);
    });

    // ng-select default config
    this.translateService
      .stream('select.not.found')
      .pipe(untilDestroyed(this))
      .subscribe((notFoundText) => {
        // This config is consulted whenever new ng-select component is created
        // Hence, if the text must be translated when user changes preferred language, as for ng-select(s) on current page,
        // then opt to parametizing the ng-select itself with `notFoundText="{{ 'select.not.found' | translate }}"`.
        // This setup will work as expected for ng-select(s) in a modal window.
        this.ngSelectConfig.notFoundText = notFoundText;
      });

    this.translateService.onLangChange.subscribe((event) => {
      document.querySelector('html').setAttribute('lang', event.lang);
    });
  }

  hideApplicationLoader() {
    // Hide the application loader
    document.body.classList.add('application-loader--fadeOut');

    setTimeout(function () {
      document.body.classList.add('application-loader--hidden');
      document.body.classList.remove('application-loader--fadeOut');
    }, 250);
  }
}
